import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import * as Dlg from '@radix-ui/react-dialog'
import * as Tb from '@radix-ui/react-tabs'
import * as Tt from '@radix-ui/react-tooltip'
import { Icon } from '@/ds/icons'
import { ResizeHandles, useResizable } from './resizable'

export function Drawer({
  open,
  onClose,
  title,
  children,
}: {
  open: boolean
  onClose: () => void
  title: ReactNode
  children: ReactNode
}) {
  /* Панель прижата вправо, поэтому тянется только левая граница, и ширина у
     всех карточек одна: это ширина боковой панели, а не свойство документа. */
  const box = useResizable({ sizeKey: 'drawer', width: 720, minWidth: 420, anchor: 'right', edges: ['left'] })

  /* Тело рисуется кадром позже — и это не украшение, а причина рывка.
     Измерено на стенде: клик → 74 мс синхронной отрисовки карточки → и только
     на 108-й миллисекунде начинался въезд панели. Человек видел задержку и
     скачок вместо движения.

     Кадр задержки отдаёт первый кадр анимации композитору (едет transform, он
     не зависит от главного потока), а тяжёлая отрисовка идёт уже во время
     движения. Заглушка на этот кадр держит высоту, чтобы содержимое не
     подбрасывало панель, когда придёт. */
  const [ready, setReady] = useState(false)
  useEffect(() => {
    if (!open) { setReady(false); return }
    const id = requestAnimationFrame(() => setReady(true))
    return () => cancelAnimationFrame(id)
  }, [open])

  return (
    <Dlg.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <Dlg.Portal>
        <Dlg.Overlay className="overlay" />
        <Dlg.Content className="drawer" aria-describedby={undefined} ref={box.ref} style={{ width: box.width }}>
          <ResizeHandles box={box} />
          <div className="drawer__head">
            <Dlg.Title className="drawer__title">{title}</Dlg.Title>
            <Dlg.Close asChild>
              <button className="icon-btn" aria-label="Закрыть">
                <Icon name="x" />
              </button>
            </Dlg.Close>
          </div>
          <div className="drawer__body">{ready ? children : <DrawerSkeleton />}</div>
        </Dlg.Content>
      </Dlg.Portal>
    </Dlg.Root>
  )
}

/** Кадр ожидания панели: полосы вместо содержимого, той же высоты. */
function DrawerSkeleton() {
  return (
    <div aria-hidden style={{ padding: 20, display: 'grid', gap: 14 }}>
      {[70, 40, 92, 60, 84, 50, 88, 46].map((w, i) => (
        <span key={i} className="skel-cell" style={{ width: w + '%', height: i === 0 ? 18 : 10 }} />
      ))}
    </div>
  )
}

export function Dialog({
  open,
  onClose,
  title,
  sizeKey = 'dialog',
  width = 560,
  children,
}: {
  open: boolean
  onClose: () => void
  title: ReactNode
  /** Под каким ключом запоминается размер. Один на все окна общего вида;
      экран передаёт своё имя, если его окно живёт своей шириной. */
  sizeKey?: string
  /** Ширина по умолчанию, пока размер не запомнен. Печатной форме нужна
      бумажная, а не разговорная. */
  width?: number
  children: ReactNode
}) {
  const box = useResizable({ sizeKey, width, edges: ['left', 'right', 'bottom', 'bottom-left', 'bottom-right'] })
  return (
    <Dlg.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <Dlg.Portal>
        <Dlg.Overlay className="overlay" />
        <Dlg.Content
          className="dialog"
          aria-describedby={undefined}
          ref={box.ref}
          style={{ width: box.width, height: box.height ?? undefined }}
        >
          <ResizeHandles box={box} />
          <div className="dialog__head">
            <Dlg.Title className="dialog__title">{title}</Dlg.Title>
            <Dlg.Close asChild>
              <button className="icon-btn" aria-label="Закрыть">
                <Icon name="x" />
              </button>
            </Dlg.Close>
          </div>
          <div className="dialog__body">{children}</div>
        </Dlg.Content>
      </Dlg.Portal>
    </Dlg.Root>
  )
}

export interface TabDef {
  value: string
  label: ReactNode
  content: ReactNode
}
export function Tabs({ tabs, defaultValue }: { tabs: TabDef[]; defaultValue?: string }) {
  return (
    <Tb.Root defaultValue={defaultValue ?? tabs[0]?.value}>
      <Tb.List className="tabs__list">
        {tabs.map((t) => (
          <Tb.Trigger key={t.value} value={t.value} className="tabs__trigger">
            {t.label}
          </Tb.Trigger>
        ))}
      </Tb.List>
      {tabs.map((t) => (
        <Tb.Content key={t.value} value={t.value} style={{ paddingTop: 'var(--space-10)' }}>
          {t.content}
        </Tb.Content>
      ))}
    </Tb.Root>
  )
}

/** Provider живёт в корне приложения (main.tsx), а не здесь: свой на каждую
    подсказку отменяет пропуск задержки между соседними. */
export const TooltipProvider = Tt.Provider

/** side по умолчанию «сверху» — как в Radix; вертикальному столбцу иконок нужен
    «справа», иначе подсказка ложится на соседний пункт. */
export function Tooltip({ label, children, side = 'top' }: { label: ReactNode; children: ReactNode; side?: 'top' | 'right' | 'bottom' | 'left' }) {
  return (
    <Tt.Root>
      <Tt.Trigger asChild>{children}</Tt.Trigger>
      <Tt.Portal>
        <Tt.Content className="tooltip" side={side} sideOffset={4}>
          {label}
        </Tt.Content>
      </Tt.Portal>
    </Tt.Root>
  )
}
